/**
 * useProfileChangeInvalidation — Forces re-fetch of dependent data
 * when profile changes. Uses startSync/tickSync/endSync for progress tracking.
 * Mount this hook once in a high-level layout component.
 */

import { useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { onProfileChange } from "@/lib/profileChangeEmitter";
import { startSync, tickSync, endSync } from "@/lib/profileSyncState";

const PROFILE_DEPENDENT_KEYS = [
  ["profile"],
  ["athlete-profile"],
  ["programs"],
  ["current-program"],
  ["planning"],
  ["sessions"],
  ["slot-proposals"],
  ["obsolescence"],
  ["progression"],
  ["checkins"],
  ["brain-alerts"],
  ["unified-planning"],
  ["user-intent-profile"],
] as const;

export function useProfileChangeInvalidation() {
  const queryClient = useQueryClient();

  const handleProfileChange = useCallback(async () => {
    const total = PROFILE_DEPENDENT_KEYS.length;
    startSync(total);

    try {
      for (const key of PROFILE_DEPENDENT_KEYS) {
        await queryClient.invalidateQueries({ queryKey: [...key] });
        tickSync();
      }
      endSync(false);
    } catch (err) {
      console.error("[useProfileChangeInvalidation] sync error", err);
      endSync(true);
    }
  }, [queryClient]);

  useEffect(() => {
    return onProfileChange(handleProfileChange);
  }, [handleProfileChange]);
}
